"use client";

import { SLAnimatedAnrufenButton } from "@/components/buttons/SLAnimatedAnrufenButton";
import { SLLanguageSwitcher } from "@/components/i18n/SLLanguageSwitcher";
import { SLLink } from "@/components/i18n/SLLink";
import { NavigationItem } from "@/helpers/enums";
import { useMessages } from "@/lib/i18n/useMessages";
import { navigationItems } from "@/lib/navigationItems";
import type { Messages } from "@/messages/buildCatalog";
import { Box, Button, Link, Typography, useTheme } from "@mui/material";

type NavItem = { id: string | null; name: string };

const NAV_LABEL_KEY: Partial<
  Record<
    NavigationItem,
    keyof Pick<Messages["nav"], "expertise" | "about" | "contact">
  >
> = {
  [NavigationItem.Expertise]: "expertise",
  [NavigationItem.About]: "about",
  [NavigationItem.Contact]: "contact",
};

function navItemLabel(
  item: (typeof navigationItems)[keyof typeof navigationItems],
  nav: Messages["nav"],
): string {
  if (!item.id) {
    return nav.blog;
  }
  const key = NAV_LABEL_KEY[item.id as NavigationItem];
  return key ? nav[key] : item.name;
}

function navItemTextDecoration(
  item: (typeof navigationItems)[keyof typeof navigationItems],
  pathname: string,
  isPageInView: (id: string) => boolean,
  areNavigationItemsInView: Record<NavigationItem, boolean | undefined>,
): "underline" | "none" {
  if (!item.id) {
    return pathname.startsWith("/blog") ? "underline" : "none";
  }
  return isPageInView(item.id) ||
    areNavigationItemsInView[item.id as NavigationItem]
    ? "underline"
    : "none";
}

export function SLNavigationMainItems({
  pathname,
  navFontSize,
  isCurrentPage,
  isPageInView,
  areNavigationItemsInView,
  onNavItemClick,
  languageVariant,
  isMobile,
}: {
  pathname: string;
  navFontSize: string;
  isCurrentPage: (item: NavItem) => boolean;
  isPageInView: (id: string) => boolean;
  areNavigationItemsInView: Record<NavigationItem, boolean | undefined>;
  onNavItemClick: (event: React.MouseEvent<HTMLDivElement>, id: string) => void;
  languageVariant: "icon" | "fullWidth";
  isMobile: boolean;
}) {
  const theme = useTheme();
  const nav = useMessages().nav;

  return (
    <>
      {Object.values(navigationItems).map((item) => {
        const textDecoration = navItemTextDecoration(
          item,
          pathname,
          isPageInView,
          areNavigationItemsInView,
        );
        const labelTypographySx = {
          fontSize: navFontSize,
          textAlign: "center" as const,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          textUnderlineOffset: "8px",
          textDecoration,
        };

        return item.id ? (
          <Button
            key={item.id}
            id={
              item.id === NavigationItem.Expertise
                ? "expertise-button"
                : item.id === NavigationItem.About
                  ? "about-button"
                  : undefined
            }
            component={Box}
            size="large"
            aria-current={isCurrentPage(item) ? "page" : undefined}
            sx={{
              width: "100%",
              display: "flex",
              alignSelf: "center",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: theme.palette.text.primary,
            }}
            LinkComponent={SLLink}
            onClick={(event) => onNavItemClick(event, item.id!)}
          >
            <Typography sx={labelTypographySx}>
              {navItemLabel(item, nav)}
            </Typography>
          </Button>
        ) : (
          <Link
            key="blog"
            component={SLLink}
            href={item.href ?? ""}
            aria-current={isCurrentPage(item) ? "page" : undefined}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              alignSelf: "center",
              height: "100%",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "none",
              },
            }}
          >
            <Button
              key={item.name}
              component={Box}
              size="large"
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                color: theme.palette.text.primary,
                "&:hover": {
                  textDecoration: "none",
                },
              }}
            >
              <Typography sx={labelTypographySx}>
                {navItemLabel(item, nav)}
              </Typography>
            </Button>
          </Link>
        );
      })}
      <SLAnimatedAnrufenButton />

      {!isMobile && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            height: "100%",
            position: "relative",
            backgroundColor: "transparent",
            borderRadius: `${theme.shape.borderRadius}px`,
            overflow: "clip",
            pr: 2,
          }}
        >
          <SLLanguageSwitcher variant={languageVariant} />
        </Box>
      )}
    </>
  );
}
